import React, { useEffect, useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AzureAD } from 'react-aad-msal'
import { authProvider } from '../../../services/authProvider'
import store from '../../../store/configureStore'
import { Container, Row, Col, Input, Button, Badge } from 'reactstrap'
import PageContainer from '../../Shared/LayoutComponents/PageContainer'
import PageHeading from '../../Shared/LayoutComponents/PageHeading'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import {
  fetchTenants,
  unmapTenant,
  exportActiveTenantsCsv,
} from '../../actions/cspRenewalsActions'
import RecipientsModal, { splitOverrideEmails } from './RecipientsModal'
import TenantSettingsDrawer from './TenantSettingsDrawer'
import TenantMappingPicker from './TenantMappingPicker'

const recipientCount = (t) => {
  const active = (t.contacts || []).filter((c) => !c.isExcluded).length
  return active + splitOverrideEmails(t.contactOverrideEmail).length
}

// Renewal-eligible: DD reports Active AND we have active subs AND admin
// hasn't disabled the tenant. This is the steady-state operational set -
// what the dispatcher will actually consider on its next run. The Inactive
// and Disabled views show the tenants that fall outside this set.
const isRenewalEligible = (t) =>
  t.isActive && t.hasActiveSubscriptions && !t.renewalsDisabled

const VALID_VIEWS = ['all', 'unmapped', 'noRecipients', 'inactive', 'disabled']

const CSPTenantsInner = ({ isAdmin }) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const tenants = useSelector((state) => state.cspRenewals.tenants)
  const updatingTenantId = useSelector((state) => state.cspRenewals.updatingTenantId)

  // View tabs are mutually exclusive filters. Backend always returns the
  // full tenant set (includeInactive=true); the view buttons narrow it
  // client-side so switching views is instant and counts are accurate.
  // Initial view honours ?view= query param so the dashboard's unmapped CTA
  // can deep-link straight to the Unmapped tab.
  const [view, setView] = useState(() => {
    const params = new URLSearchParams(location.search)
    const requested = params.get('view')
    return VALID_VIEWS.includes(requested) ? requested : 'all'
  })
  const [searchText, setSearchText] = useState('')
  const [rowState, setRowState] = useState({})
  const [recipientsTenantId, setRecipientsTenantId] = useState(null)
  const [settingsTenantId, setSettingsTenantId] = useState(null)
  const [exporting, setExporting] = useState(false)

  const handleExport = async () => {
    setExporting(true)
    try {
      await dispatch(exportActiveTenantsCsv())
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    // Always fetch the full set so client-side view filters can compute
    // counts + content for every tab without re-hitting the API.
    dispatch(fetchTenants(true))
  }, [dispatch])

  const getRowState = (tenantId) =>
    rowState[tenantId] || { searchText: '', companyId: '', overrideOpen: false }
  const updateRowState = (tenantId, patch) => {
    setRowState((prev) => ({ ...prev, [tenantId]: { ...getRowState(tenantId), ...patch } }))
  }

  // Each view derives from the single fetched list. Counts on the buttons
  // reflect these sets exactly.
  const allTenants = tenants || []
  const eligibleTenants = useMemo(
    () => allTenants.filter(isRenewalEligible),
    [allTenants]
  )
  const unmappedTenants = useMemo(
    () => eligibleTenants.filter((t) => !t.companyId),
    [eligibleTenants]
  )
  const noRecipientsTenants = useMemo(
    () => eligibleTenants.filter((t) => t.companyId && recipientCount(t) === 0),
    [eligibleTenants]
  )
  const inactiveTenants = useMemo(
    () => allTenants.filter((t) => !t.isActive || !t.hasActiveSubscriptions),
    [allTenants]
  )
  const disabledTenants = useMemo(
    () => allTenants.filter((t) => t.renewalsDisabled),
    [allTenants]
  )

  const baseTenants = useMemo(() => {
    switch (view) {
      case 'unmapped': return unmappedTenants
      case 'noRecipients': return noRecipientsTenants
      case 'inactive': return inactiveTenants
      case 'disabled': return disabledTenants
      default: return eligibleTenants
    }
  }, [view, eligibleTenants, unmappedTenants, noRecipientsTenants, inactiveTenants, disabledTenants])

  const visibleTenants = useMemo(() => {
    const q = searchText.trim().toLowerCase()
    if (!q) return baseTenants
    return baseTenants.filter((t) =>
      [t.tenantName, t.companyName, t.domainPrefix, t.tenantId]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q))
    )
  }, [baseTenants, searchText])

  const handleUnmap = (tenant) => {
    if (!isAdmin) return
    dispatch(unmapTenant(tenant.tenantId, true))
  }

  const ViewButton = ({ value, label, count, color = 'primary' }) => (
    <button
      type="button"
      className={`btn ${view === value ? `btn-${color}` : `btn-outline-${color}`}`}
      onClick={() => setView(value)}
    >
      {label}
      {count > 0 && (
        <Badge color={view === value ? 'light' : color} className={`ml-1 ${view === value ? 'text-dark' : ''}`}>
          {count}
        </Badge>
      )}
    </button>
  )

  return (
    <PageContainer>
      <Container fluid className="mt-3 mb-4">
        <Row className="align-items-center mb-3">
          <Col>
            <Link to="/CSPRenewals" className="small text-muted">&larr; Back to CSP Renewals</Link>
            <PageHeading>CSP Tenant Settings</PageHeading>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              color="outline-secondary"
              onClick={handleExport}
              disabled={exporting || !tenants}
              title="Download a CSV of all active tenants with their CW mapping + recipients. Hits CW live for company Identifier + Territory, so may take a few seconds."
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </Col>
        </Row>

        <p className="text-muted small">
          Map Dicker Data tenants to ConnectWise / Essentials companies. Tenants are
          auto-matched on every sync via Azure DirectoryID; use Override for any tenant
          that needs a different mapping, or Manage Recipients to choose which contacts
          receive renewal notices.
        </p>

        <Row className="mb-3 align-items-center">
          <Col md={4} className="mb-2 mb-md-0">
            <Input
              type="search"
              placeholder="Search by tenant name, company, domain, or ID..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              bsSize="sm"
            />
          </Col>
          <Col md={8}>
            <div className="btn-group btn-group-sm" role="group">
              <ViewButton value="all" label="All" count={eligibleTenants.length} />
              <ViewButton value="unmapped" label="Unmapped" count={unmappedTenants.length} />
              <ViewButton
                value="noRecipients"
                label="No recipients"
                count={noRecipientsTenants.length}
                color="danger"
              />
              <ViewButton
                value="inactive"
                label="Inactive"
                count={inactiveTenants.length}
                color="secondary"
              />
              <ViewButton
                value="disabled"
                label="Disabled"
                count={disabledTenants.length}
                color="danger"
              />
            </div>
          </Col>
        </Row>

        {!tenants ? (
          <LoadingSpinner />
        ) : visibleTenants.length === 0 ? (
          <p className="text-muted">
            {searchText
              ? `No tenants match "${searchText}".`
              : view === 'unmapped'
                ? 'All tenants are mapped.'
                : view === 'noRecipients'
                  ? 'All mapped tenants have at least one recipient.'
                  : view === 'inactive'
                    ? 'No inactive tenants.'
                    : view === 'disabled'
                      ? 'No tenants have renewals disabled.'
                      : 'No tenants found.'}
          </p>
        ) : (
          visibleTenants.map((t) => {
            const s = getRowState(t.tenantId)
            const isUpdating = updatingTenantId === t.tenantId
            const isMapped = !!t.companyId

            return (
              <div key={t.tenantId} className="border rounded p-3 mb-2 bg-white">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <strong>{t.tenantName || t.tenantId}</strong>
                    {t.domainPrefix && (
                      <span className="text-muted ml-2 small">({t.domainPrefix})</span>
                    )}
                    {!t.isActive && (
                      <Badge color="secondary" className="ml-2">Inactive at DD</Badge>
                    )}
                    {t.isActive && !t.hasActiveSubscriptions && (
                      <Badge color="secondary" className="ml-2">No active subscriptions</Badge>
                    )}
                    {t.renewalsDisabled && (
                      <Badge color="danger" className="ml-2" title="Renewals disabled — dispatcher skips this tenant entirely">
                        Renewals disabled
                      </Badge>
                    )}
                    <div className="small text-muted">
                      {t.activeSubscriptionCount ?? 0} active subscription{t.activeSubscriptionCount === 1 ? '' : 's'}
                      <Button
                        size="sm"
                        color="link"
                        className="ml-2 p-0"
                        onClick={() => setSettingsTenantId(t.tenantId)}
                        title="Tenant settings (enable / disable renewal emails)"
                      >
                        Settings
                      </Button>
                    </div>
                    {isMapped && (
                      <>
                        <div className="mt-1 small">
                          Mapped to <strong>{t.companyName}</strong>
                          {t.isManuallyMapped && (
                            <Badge color="warning" className="ml-2">Override</Badge>
                          )}
                          {isAdmin && (
                            <>
                              <Button
                                size="sm"
                                color="link"
                                className="ml-2 p-0"
                                disabled={isUpdating}
                                onClick={() => updateRowState(t.tenantId, { overrideOpen: !s.overrideOpen, searchText: '', companyId: '' })}
                              >
                                {s.overrideOpen ? 'Cancel' : 'Override'}
                              </Button>
                              <Button
                                size="sm"
                                color="link"
                                className="ml-2 p-0"
                                disabled={isUpdating}
                                onClick={() => handleUnmap(t)}
                              >
                                Unmap
                              </Button>
                            </>
                          )}
                        </div>
                        {(() => {
                          const total = recipientCount(t)
                          return (
                            <div className="d-flex align-items-center mt-1 small">
                              {total === 0 ? (
                                <Badge color="danger">No recipients</Badge>
                              ) : (
                                <span className="text-muted">
                                  {total} recipient{total === 1 ? '' : 's'}
                                </span>
                              )}
                              <Button
                                size="sm"
                                color="link"
                                className="ml-2 p-0"
                                onClick={() => setRecipientsTenantId(t.tenantId)}
                              >
                                Manage Recipients
                              </Button>
                            </div>
                          )
                        })()}
                      </>
                    )}
                  </div>
                </div>

                {isAdmin && (!isMapped || s.overrideOpen) && (
                  <div className="mt-2">
                    <TenantMappingPicker
                      ddTenantId={t.tenantId}
                      includeInactive={true}
                      submitLabel={s.overrideOpen ? 'Save Override' : 'Map'}
                      onMapped={() => {
                        // Collapse the override panel after a successful save;
                        // tenants refetch is triggered by the action itself.
                        if (s.overrideOpen) {
                          updateRowState(t.tenantId, { overrideOpen: false, searchText: '', companyId: '' })
                        }
                      }}
                      onCancel={s.overrideOpen ? () =>
                        updateRowState(t.tenantId, { overrideOpen: false, searchText: '', companyId: '' })
                        : undefined}
                    />
                  </div>
                )}
              </div>
            )
          })
        )}
      </Container>

      <RecipientsModal
        isOpen={recipientsTenantId !== null}
        toggle={() => setRecipientsTenantId(null)}
        tenantId={recipientsTenantId}
        isAdmin={isAdmin}
        includeInactive={true}
      />

      <TenantSettingsDrawer
        isOpen={settingsTenantId !== null}
        toggle={() => setSettingsTenantId(null)}
        tenantId={settingsTenantId}
        isAdmin={isAdmin}
        includeInactive={true}
      />
    </PageContainer>
  )
}

const CSPTenants = () => {
  return (
    <AzureAD provider={authProvider} reduxStore={store}>
      {({ accountInfo }) => {
        const roles = accountInfo?.account?.idToken?.roles || []
        const isAdmin = roles.includes('Admin')
        const isSales = roles.includes('Sales')

        if (!isAdmin && !isSales) {
          return (
            <PageContainer>
              <Container fluid className="mt-3">
                <p>You do not have permission to view this page.</p>
              </Container>
            </PageContainer>
          )
        }

        // Sales has full edit rights inside the CSP renewals dashboard.
        return <CSPTenantsInner isAdmin={isAdmin || isSales} />
      }}
    </AzureAD>
  )
}

export default CSPTenants
